import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  VStack,
  HStack,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  IconButton,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  useToast,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  useColorModeValue,
  Badge,
  Tooltip,
  Spinner,
  Alert,
  AlertIcon,
} from '@chakra-ui/react';
import { FiPlus, FiEdit2, FiTrash2, FiEye } from 'react-icons/fi';
import { originalPromptsApi } from '../services/originalPromptsApi';

const OriginalPromptManager = ({ projectId }) => {
  const [prompts, setPrompts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    content: '',
    description: ''
  });

  const { isOpen: isCreateOpen, onOpen: onCreateOpen, onClose: onCreateClose } = useDisclosure();
  const { isOpen: isEditOpen, onOpen: onEditOpen, onClose: onEditClose } = useDisclosure();
  const { isOpen: isViewOpen, onOpen: onViewOpen, onClose: onViewClose } = useDisclosure();
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  
  const toast = useToast();
  const cancelRef = React.useRef();

  // プロンプト一覧を取得
  const fetchPrompts = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await originalPromptsApi.getOriginalPrompts(projectId);
      setPrompts(data);
    } catch (error) {
      console.error('オリジナルプロンプト取得エラー:', error);
      setError('プロンプトの取得に失敗しました');
      toast({
        title: 'エラー',
        description: 'プロンプトの取得に失敗しました',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPrompts();
  }, [projectId]);

  // フォームをリセット
  const resetForm = () => {
    setFormData({
      name: '',
      content: '',
      description: ''
    });
    setSelectedPrompt(null);
  };

  // 新規作成モーダルを開く
  const handleCreateOpen = () => {
    resetForm();
    onCreateOpen();
  };

  // 編集モーダルを開く
  const handleEditOpen = (prompt) => {
    setSelectedPrompt(prompt);
    setFormData({
      name: prompt.name,
      content: prompt.content,
      description: prompt.description || ''
    });
    onEditOpen();
  };

  // 詳細表示モーダルを開く
  const handleViewOpen = (prompt) => {
    setSelectedPrompt(prompt);
    onViewOpen();
  };

  // 削除確認モーダルを開く
  const handleDeleteOpen = (prompt) => {
    setSelectedPrompt(prompt);
    onDeleteOpen();
  };

  // プロンプトを作成
  const handleCreate = async () => {
    try {
      await originalPromptsApi.createOriginalPrompt(projectId, formData);
      toast({
        title: '成功',
        description: 'オリジナルプロンプトを作成しました',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      onCreateClose();
      fetchPrompts();
    } catch (error) {
      console.error('プロンプト作成エラー:', error);
      toast({
        title: 'エラー',
        description: 'プロンプトの作成に失敗しました',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // プロンプトを更新
  const handleUpdate = async () => {
    try {
      await originalPromptsApi.updateOriginalPrompt(projectId, selectedPrompt.id, formData);
      toast({
        title: '成功',
        description: 'オリジナルプロンプトを更新しました',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      onEditClose();
      fetchPrompts();
    } catch (error) {
      console.error('プロンプト更新エラー:', error);
      toast({
        title: 'エラー',
        description: 'プロンプトの更新に失敗しました',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // プロンプトを削除
  const handleDelete = async () => {
    try {
      await originalPromptsApi.deleteOriginalPrompt(projectId, selectedPrompt.id);
      toast({
        title: '成功',
        description: 'オリジナルプロンプトを削除しました',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      onDeleteClose();
      fetchPrompts();
    } catch (error) {
      console.error('プロンプト削除エラー:', error);
      toast({
        title: 'エラー',
        description: 'プロンプトの削除に失敗しました',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // 入力値の変更処理
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  if (isLoading) {
    return (
      <Box textAlign="center" py={10}>
        <Spinner size="lg" />
        <Text mt={4}>プロンプトを読み込み中...</Text>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert status="error">
        <AlertIcon />
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      {/* ヘッダー */}
      <HStack justify="space-between" mb={6}>
        <Text fontSize="lg" fontWeight="bold">オリジナルプロンプト管理</Text>
        <Button
          leftIcon={<FiPlus />}
          colorScheme="blue"
          onClick={handleCreateOpen}
        >
          新規作成
        </Button>
      </HStack>

      {/* プロンプト一覧テーブル */}
      {prompts.length === 0 ? (
        <Box
          textAlign="center"
          py={10}
          bg={useColorModeValue('gray.50', 'gray.700')}
          borderRadius="md"
        >
          <Text color="gray.500">
            オリジナルプロンプトが登録されていません。
            <br />
            「新規作成」ボタンから最初のプロンプトを作成してください。
          </Text>
        </Box>
      ) : (
        <Box overflowX="auto">
          <Table variant="simple">
            <Thead>
              <Tr>
                <Th>名前</Th>
                <Th>説明</Th>
                <Th>作成日時</Th>
                <Th>更新日時</Th>
                <Th width="120px">操作</Th>
              </Tr>
            </Thead>
            <Tbody>
              {prompts.map((prompt) => (
                <Tr key={prompt.id}>
                  <Td>
                    <Text fontWeight="semibold">{prompt.name}</Text>
                  </Td>
                  <Td>
                    <Text color="gray.600" noOfLines={2}>
                      {prompt.description || '説明なし'}
                    </Text>
                  </Td>
                  <Td>
                    <Text fontSize="sm">
                      {new Date(prompt.created_at).toLocaleString('ja-JP')}
                    </Text>
                  </Td>
                  <Td>
                    <Text fontSize="sm">
                      {new Date(prompt.updated_at).toLocaleString('ja-JP')}
                    </Text>
                  </Td>
                  <Td>
                    <HStack spacing={1}>
                      <Tooltip label="詳細表示">
                        <IconButton
                          icon={<FiEye />}
                          size="sm"
                          variant="ghost"
                          onClick={() => handleViewOpen(prompt)}
                        />
                      </Tooltip>
                      <Tooltip label="編集">
                        <IconButton
                          icon={<FiEdit2 />}
                          size="sm"
                          variant="ghost"
                          onClick={() => handleEditOpen(prompt)}
                        />
                      </Tooltip>
                      <Tooltip label="削除">
                        <IconButton
                          icon={<FiTrash2 />}
                          size="sm"
                          variant="ghost"
                          colorScheme="red"
                          onClick={() => handleDeleteOpen(prompt)}
                        />
                      </Tooltip>
                    </HStack>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      )}

      {/* 新規作成モーダル */}
      <Modal isOpen={isCreateOpen} onClose={onCreateClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>オリジナルプロンプト作成</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>プロンプト名</FormLabel>
                <Input
                  placeholder="プロンプト名を入力"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                />
              </FormControl>

              <FormControl>
                <FormLabel>説明</FormLabel>
                <Input
                  placeholder="プロンプトの説明を入力（任意）"
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>プロンプト内容</FormLabel>
                <Textarea
                  placeholder="プロンプトの内容を入力"
                  value={formData.content}
                  onChange={(e) => handleInputChange('content', e.target.value)}
                  minH="200px"
                  fontFamily="monospace"
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onCreateClose}>
              キャンセル
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleCreate}
              isDisabled={!formData.name || !formData.content}
            >
              作成
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* 編集モーダル */}
      <Modal isOpen={isEditOpen} onClose={onEditClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>オリジナルプロンプト編集</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>プロンプト名</FormLabel>
                <Input
                  placeholder="プロンプト名を入力"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                />
              </FormControl>

              <FormControl>
                <FormLabel>説明</FormLabel>
                <Input
                  placeholder="プロンプトの説明を入力（任意）"
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>プロンプト内容</FormLabel>
                <Textarea
                  placeholder="プロンプトの内容を入力"
                  value={formData.content}
                  onChange={(e) => handleInputChange('content', e.target.value)}
                  minH="200px"
                  fontFamily="monospace"
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onEditClose}>
              キャンセル
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleUpdate}
              isDisabled={!formData.name || !formData.content}
            >
              更新
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* 詳細表示モーダル */}
      <Modal isOpen={isViewOpen} onClose={onViewClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>プロンプト詳細</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {selectedPrompt && (
              <VStack spacing={4} align="stretch">
                <Box>
                  <Text fontWeight="bold" mb={2}>プロンプト名</Text>
                  <Text>{selectedPrompt.name}</Text>
                </Box>

                {selectedPrompt.description && (
                  <Box>
                    <Text fontWeight="bold" mb={2}>説明</Text>
                    <Text>{selectedPrompt.description}</Text>
                  </Box>
                )}

                <Box>
                  <Text fontWeight="bold" mb={2}>プロンプト内容</Text>
                  <Box
                    p={4}
                    bg={useColorModeValue('gray.50', 'gray.700')}
                    borderRadius="md"
                    maxH="300px"
                    overflowY="auto"
                  >
                    <Text fontFamily="monospace" whiteSpace="pre-wrap">
                      {selectedPrompt.content}
                    </Text>
                  </Box>
                </Box>

                <HStack spacing={4}>
                  <Box>
                    <Text fontWeight="bold" fontSize="sm">作成日時</Text>
                    <Text fontSize="sm">
                      {new Date(selectedPrompt.created_at).toLocaleString('ja-JP')}
                    </Text>
                  </Box>
                  <Box>
                    <Text fontWeight="bold" fontSize="sm">更新日時</Text>
                    <Text fontSize="sm">
                      {new Date(selectedPrompt.updated_at).toLocaleString('ja-JP')}
                    </Text>
                  </Box>
                </HStack>
              </VStack>
            )}
          </ModalBody>
          <ModalFooter>
            <Button onClick={onViewClose}>閉じる</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* 削除確認ダイアログ */}
      <AlertDialog
        isOpen={isDeleteOpen}
        leastDestructiveRef={cancelRef}
        onClose={onDeleteClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              プロンプトの削除
            </AlertDialogHeader>

            <AlertDialogBody>
              「{selectedPrompt?.name}」を削除してもよろしいですか？
              <br />
              この操作は元に戻せません。
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onDeleteClose}>
                キャンセル
              </Button>
              <Button colorScheme="red" onClick={handleDelete} ml={3}>
                削除
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default OriginalPromptManager;